import { useEffect, useState } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ListItemNode, ListNode } from '@lexical/list'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { LinkNode } from '@lexical/link'
import { CodeNode } from '@lexical/code'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import RteToolbarPlugin from '../../utils/RteToolbar'
import { useTranslation } from 'react-i18next'
import './Editor.css'

const theme = {}

function onError (error) {
  console.error(error)
}

// 🎛 Plugin to control editor editability
function EditableTogglePlugin ({ isEditing }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    editor.setEditable(isEditing)
  }, [editor, isEditing])

  return null
}

// 🧠 Plugin to handle "Save" logic
function SavePlugin ({ onSave }) {
  const { t } = useTranslation('events')
  const [editor] = useLexicalComposerContext()

  const handleSave = () => {
    editor.update(() => {
      const editorState = editor.getEditorState()
      const serialized = JSON.stringify(editorState) // Lexical JSON format
      onSave(serialized)
    })
  }

  return (
    <button onClick={handleSave}>
      {t('editor.save')}
    </button>
  )
}

export default function RichTextViewer ({ content, isEditing, onSave }) {
  const { t } = useTranslation('events')
  const isControlled = isEditing !== undefined
  const [internalEditing, setInternalEditing] = useState(false)
  const [savedContent, setSavedContent] = useState(content)

  const editing = isControlled ? isEditing : internalEditing

  useEffect(() => {
    setSavedContent(content)
  }, [content])

  const initialConfig = {
    namespace: 'RichTextViewer',
    theme,
    onError,
    nodes: [HeadingNode, ListItemNode, ListNode, QuoteNode, LinkNode, CodeNode],
    editorState:
      typeof savedContent === 'string'
        ? savedContent
        : JSON.stringify(savedContent),
    editable: false
  }

  const handleSave = (serializedState) => {
    setSavedContent(serializedState)
    if (!isControlled) setInternalEditing(false) // exit edit mode after saving
    if (onSave) onSave(serializedState)
  }

  return (
    <Box sx={{ padding: 1 }}>
      {!isControlled && (
        <Button
          className='edit-toggle'
          onClick={() => setInternalEditing(!internalEditing)}
        >
          {editing ? t('editor.cancel') : t('editor.edit')}
        </Button>
      )}

      <LexicalComposer initialConfig={initialConfig}>
        {editing && <RteToolbarPlugin />}
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className='editor-input'
              style={{
                backgroundColor: editing ? '#111' : 'transparent',
                outline: 'none',
                border: 'none',
                minHeight: '200px',
                padding: '8px',
                borderRadius: '4px',
                fontFamily: 'inherit'
              }}
            />
          }
          placeholder={null}
        />
        <EditableTogglePlugin isEditing={editing} />
        {editing && <SavePlugin onSave={handleSave} />}
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
      </LexicalComposer>
    </Box>
  )
}
